import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { reactive, ref, computed, watch, PropType, nextTick } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import CmTable from '@/components/cmTable/index.vue'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'

import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import GroupTreeSelector from '@/views/dashboard/_components/group-tree-selector/group-tree-selector.vue'
import IPColumn from '@/views/dashboard/_components/ip-column/IPColumn.vue'
import PolicySelect from '@/views/dashboard/_components/policy-select/policy-select.vue'
import {
  TerminalNetIpLimitListService,
  RequestParams,
  IpLogRow,
} from '@/views/dashboard/manage/ip-address-blocking/service'
import { ActionState } from '@/views/dashboard/manage/ip-address-blocking/types'
import {
  getActionStateLabelMap,
  getDirectionStateLabelMap,
  getProtocolStateLabelMap,
} from '@/views/dashboard/manage/ip-address-blocking/ip-address-blocking-labels'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { useRoute } from 'vue-router'
import { PolicyType } from '@/views/dashboard/_enum/policy-type'
import { TerminalType } from '@/views/dashboard/_enum/terminal-type'
import { formatMapToOptions } from '@/utils/tool'
import {
  ActionType,
  FormSize,
  FormType,
  ObjectFormItem,
  DateType,
} from '@/components/cmForm/types'
import { TableOpt, TableActOpt } from '@/components/cmTable/types'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'
import { computerInfoCols } from '@/views/dashboard/data-safe/ransomware-protection/types/ransomware-incident'
import dayjs from 'dayjs'
import { useParseTime } from '@/hooks'
import { ServicePermissionService, PermissionFe } from '@/services/service-permission.service'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import { formatTimeStamp } from '@/utils/time'

type IpAccessEventQueryParams = Partial<Omit<RequestParams, 'group_id'>> & {
  group_id?: number | string
  date?: [string, string]
}

type FilterBarKeys = 'strategy_id' | 'group_id' | 'direction' | 'action'

export default /*#__PURE__*/_defineComponent({
  __name: 'ip-access-event-list',
  props: {
  viewMode: {
    type: String as PropType<TableViewModeType>,
    default: TableViewMode.preview
  },
  /** 侧栏嵌入：日期 / 关键字 / 策略 id（如从策略设置「近一月访问事件」） */
  embedDefaults: {
    type: Object as PropType<{
      dateRange?: [string, string]
      keyWord?: string
      strategyId?: string
    } | null>,
    default: null
  },
  /** 预览模式是否展示筛选栏（用于首页等需显示搜索框并受 embedDefaults 驱动的场景） */
  showPreviewSearch: {
    type: Boolean,
    default: false
  },
},
  emits: ['viewAll', 'rawData'],
  setup(__props, { expose: __expose, emit: __emit }) {

const { t, locale } = useI18n()
const props = __props

function resolveInitialDateRange(): [string, string] | undefined {
  const ed = props.embedDefaults?.dateRange
  if (ed?.length === 2) return ed
  if (props.viewMode === TableViewMode.full) {
    return [dayjs().subtract(3, 'month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]
  }
  return undefined
}

const emits = __emit

const injector = useReflectiveInjector()
const terminalNetIpLimitListService = injector.get(TerminalNetIpLimitListService)
const servicePermissionService = injector.get(ServicePermissionService)
const dataApi = terminalNetIpLimitListService.getTerminalNetIpLimitList.bind(terminalNetIpLimitListService)
const hasPermission = servicePermissionService.getHasPermission(PermissionFe.internet_ip_limit)
const route = useRoute()
const defaultPolicyId = (route.query.policy_id as string) || undefined

function initialStrategyId() {
  return props.embedDefaults?.strategyId ?? defaultPolicyId
}

const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)
const total = computed(() => tableRef.value?.total || 0)

const initialDateRange = resolveInitialDateRange()
const [initialStartTime, initialEndTime] = useParseTime(initialDateRange || [])

/** 列表请求参数；group_id 与分组树一致，接口侧常用 -1 表示全部 */
const queryParams = reactive<IpAccessEventQueryParams>({
  strategy_id: initialStrategyId(),
  group_id: -1,
  key_word: props.embedDefaults?.keyWord ?? '',
  page: 1,
  limit: props.viewMode === TableViewMode.preview ? 10 : 20,
  start_time: initialStartTime,
  end_time: initialEndTime,
  date: initialDateRange,
  direction: null,
  action: null,
})

const filterFormColumns = computed<
  ObjectFormItem<RequestParams & { date?: [string, string] }, FilterBarKeys> & {
    date?: any
  }
>(() => {
  void locale.value
  return {
    strategy_id: {
      colOpt: { style: 'flex: 0 0 130px' },
      slot: true,
      get label() {
        return t('ipAccessEvent.triggerPolicy')
      },
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      events: {
        change: () => void refreshList(),
      },
    },
    group_id: {
      colOpt: { style: 'flex: 0 0 150px' },
      slot: true,
      label: t('portManage.group'),
      outSearchFiledFlag: true,
      hiddenAllLabel: true,
      type: FormType.Select,
      events: {
        change: () => void refreshList(),
      },
    },
    date: {
      colOpt: { style: 'flex: 0 0 260px' },
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      type: FormType.Date,
      dateType: DateType.DateRange,
      events: {
        change: (val) => {
          if (val) {
            const [start, end] = useParseTime(val)
            queryParams.start_time = start
            queryParams.end_time = end
          } else {
            queryParams.start_time = undefined
            queryParams.end_time = undefined
          }
          void refreshList()
        },
      },
      dateOpt: {
        'value-format': 'YYYY-MM-DD',
        'start-placeholder': t('portManage.startTime'),
        'end-placeholder': t('portManage.endTime'),
        clearable: true,
      },
    },
    direction: {
      colOpt: { span: 12 },
      get label() {
        return t('ipAccessEvent.interceptDirection')
      },
      type: FormType.Select,
      selectOpt: {
        clearable: true,
      },
      dictData: formatMapToOptions(getDirectionStateLabelMap(t), true),
    },
    action: {
      colOpt: { span: 12 },
      get label() {
        return t('ipAccessEvent.executeAction')
      },
      type: FormType.Select,
      selectOpt: {
        clearable: true,
      },
      dictData: formatMapToOptions(getActionStateLabelMap(t), true).filter((item) => item.value !== ActionState.just_record),
    },
  }
})

const isShowRawData = ref(false)
const currentRow = ref<any>({})

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: { label: t('portManage.operation'), width: 120, align: 'left', fixed: 'right' },
    actionButtons: (rowItem: IpLogRow) => [
      {
        name: t('portManage.dataSource'),
        type: 'rawData',
        opt: { size: FormSize.Small, text: true, type: 'text' },
      },
    ],
  }
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  const directionLabels = getDirectionStateLabelMap(t)
  const protocolLabels = getProtocolStateLabelMap(t)
  const actionLabels = getActionStateLabelMap(t)
  return {
    bodyStyle: props.viewMode === TableViewMode.full ? 'padding:10px 16px' : undefined,
    path: 'list',
    height: props.viewMode === TableViewMode.preview ? undefined : '100%',
    showActionBar: false,
    showForm: false,
    showRightToolbar: false,
    rowKey: 'device_id',
    selection: false,
    showTip: false,
    usePagination: false,
    disableScrollLoad: props.viewMode === TableViewMode.preview,
    scrollLoadThreshold: 10,
    scrollLoadDelay: 100,
    columns: [
      {
        label: t('bootOptimize.interceptTime'),
        prop: 'intercept_time',
        formatter: (_row: any, value: number) => formatTimeStamp(value, 'YYYY-MM-DD HH:mm'),
        minWidth: 150,
      },
      {
        label: t('ipAccessEvent.triggerPolicy'),
        prop: 'strategy_name',
        'min-width': 120,
        showOverflow: true,
      },
      ...computerInfoCols(true, true, t),
      {
        label: t('ipAccessEvent.operateProcess'),
        prop: 'process_name',
        'min-width': 120,
        showOverflow: true,
      },
      {
        label: t('ipAccessEvent.interceptDirection'),
        prop: 'direction',
        minWidth: 130,
        formatter: (_row: any, value: number) => directionLabels[value] || '--'
      },
      {
        label: t('ipAccessEvent.protocol'),
        prop: 'protocol',
        minWidth: 100,
        formatter: (_row: any, value: number) => protocolLabels[value] || '--'
      },
      {
        label: t('ipAccessEvent.localIpPort'),
        prop: 'local_port',
        minWidth: 170,
        formatter: (_row: any, value: number) => {
          if (_row.system_ip && value) {
            return `${_row.system_ip}:${value}`
          }
          return '--'
        }
      },
      {
        label: t('ipAccessEvent.remoteIpPort'),
        prop: 'remote_ip',
        minWidth: 160,showOverflow:true,
        formatter: (_row: any, value: string) => {
          if (value && _row.remote_port) {
            return `${value}:${_row.remote_port}`
          }
          return value || '--'
        }
      },
      {
        label: t('ipAccessEvent.executeAction'),
        prop: 'action',
        minWidth: 190,
        formatter: (_row: any, value: number) => actionLabels[value] || '--'
      },
    ],
  }
})

async function handleSearch(value: string) {
  await nextTick()
  queryParams.key_word = value
  void refreshList()
}

async function handleQuery(form: any) {
  Object.assign(queryParams, form)
  if (form.date?.length === 2) {
    const [start, end] = useParseTime(form.date)
    queryParams.start_time = start
    queryParams.end_time = end
  }
  await refreshList()
}

function handleQueryChange() {
  void refreshList()
}

function handleReset() {
  queryParams.strategy_id = initialStrategyId()
  queryParams.key_word = props.embedDefaults?.keyWord ?? ''
  queryParams.group_id = -1
  const range = resolveInitialDateRange()
  if (range?.length === 2) {
    const [s, e] = useParseTime(range)
    queryParams.date = range
    queryParams.start_time = s
    queryParams.end_time = e
  } else {
    queryParams.date = undefined
    queryParams.start_time = undefined
    queryParams.end_time = undefined
  }
  queryParams.direction = null
  queryParams.action = null
  void refreshList()
}

async function updateGroupId(groupId: number) {
  // GroupTreeSelector 初始化会回传当前分组；未变化时不重复请求
  if (queryParams.group_id === groupId) return
  queryParams.group_id = groupId
  queryParams.page = 1
  await refreshList()
}

watch(
  () => props.embedDefaults,
  (ed) => {
    const isPreviewEmbed = props.viewMode === TableViewMode.preview && props.showPreviewSearch
    const isFullEmbed = props.viewMode === TableViewMode.full
    if (!isPreviewEmbed && !isFullEmbed) return
    if (!ed) return

    let changed = false
    if (ed.keyWord !== undefined && queryParams.key_word !== ed.keyWord) {
      queryParams.key_word = ed.keyWord
      changed = true
    }
    if (ed.dateRange?.length === 2) {
      const [s, e] = useParseTime(ed.dateRange)
      queryParams.date = [ed.dateRange[0], ed.dateRange[1]]
      queryParams.start_time = s
      queryParams.end_time = e
      changed = true
    }
    if (ed.strategyId !== undefined && queryParams.strategy_id !== ed.strategyId) {
      queryParams.strategy_id = ed.strategyId || undefined
      changed = true
    }
    if (changed) {
      queryParams.page = 1
      void nextTick(() => void refreshList())
    }
  },
  { deep: true, immediate: true }
)

function handleAction({ type, rowItem }: { type: any; rowItem?: IpLogRow }) {
  if (type === 'rawData' && rowItem) {
    currentRow.value = rowItem
    isShowRawData.value = true
  }
}

function download(type: 0 | 1) {
  const params: Partial<RequestParams> = {
    strategy_id: queryParams.strategy_id,
    group_id: queryParams.group_id != null ? String(queryParams.group_id) : undefined,
    key_word: queryParams.key_word,
    start_time: queryParams.start_time,
    end_time: queryParams.end_time,
    direction: queryParams.direction,
    action: queryParams.action,
  }
  terminalNetIpLimitListService.getTerminalNetIpLimitExport(type, params, 'IP访问事件-')
}

/** 首屏仅由 CmTable 默认 firstLoading 拉一次；嵌入条件变化仍由下方 watch(embedDefaults) refresh，避免与已移除的 onMounted(refreshList) 双发 */
__expose({
  tableRef,
  getList: () => void refreshList(),
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      tableOpt: tableOpt.value,
      tableActOpt: tableActOpt.value,
      "has-permission": _unref(hasPermission),
      api: _unref(dataApi),
      modelValue: queryParams,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((queryParams) = $event)),
      pageOption: {
      style: 'padding-bottom: 16px',
      pageNumName: 'page',
      pageSizeName: 'limit',
    },
      onHandleAction: handleAction
    }, {
      header: _withCtx(() => [
        (props.viewMode !== _unref(TableViewMode).preview)
          ? (_openBlock(), _createBlock(SearchFilterBar, {
              key: 0,
              modelValue: queryParams,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryParams) = $event)),
              "form-columns": filterFormColumns.value,
              disabled: !_unref(hasPermission),
              "show-search": true,
              "sync-search-from-field": "key_word",
              "search-placeholder": _ctx.$t('ipAccessEvent.searchPlaceholder'),
              "popover-threshold": 2,
              onSearch: handleSearch,
              onQuery: handleQuery,
              onReset: handleReset
            }, {
              strategy_id: _withCtx(() => [
                _createVNode(PolicySelect, {
                  width: "100%",
                  size: "small",
                  disabled: !_unref(hasPermission),
                  modelValue: queryParams.strategy_id,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((queryParams.strategy_id) = $event)),
                  "strategy-type": _unref(PolicyType).ip_address_blocking,
                  onChange: handleQueryChange
                }, null, 8, ["disabled", "modelValue", "strategy-type"])
              ]),
              group_id: _withCtx(() => [
                _createVNode(GroupTreeSelector, {
                  disabled: !_unref(hasPermission),
                  "terminal-type": [_unref(TerminalType).windows, _unref(TerminalType).windowsServer],
                  onSelected: updateGroupId
                }, null, 8, ["disabled", "terminal-type"])
              ]),
              actions: _withCtx(() => [
                (props.viewMode === _unref(TableViewMode).full)
                  ? (_openBlock(), _createBlock(ExportDropdown, {
                      key: 0,
                      "has-permission": _unref(hasPermission),
                      onExport: download
                    }, null, 8, ["has-permission"]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["modelValue", "form-columns", "disabled", "search-placeholder"]))
          : _createCommentVNode("", true)
      ]),
      ip_address: _withCtx(({ row }) => [
        _createVNode(IPColumn, {
          cip: row.client_ip,
          sip: row.system_ip
        }, null, 8, ["cip", "sip"])
      ]),
      strategy_name: _withCtx(({ row }) => [
        _createElementVNode("span", null, _toDisplayString(row.strategy_name || '--'), 1)
      ]),
      _: 1
    }, 8, ["tableOpt", "tableActOpt", "has-permission", "api", "modelValue"]),
    _createVNode(RawDataDrawer, {
      modelValue: isShowRawData.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((isShowRawData).value = $event)),
      "raw-data": currentRow.value
    }, null, 8, ["modelValue", "raw-data"])
  ], 64))
}
}

})